import React, { useEffect, useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Autocomplete from '@mui/material/Autocomplete';
import Skeleton from '@mui/material/Skeleton';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import { apiUrl, fetchJson } from '../../apiBase';
import { normalizePhone } from '../../utils/phone';
import ParentOptInManager from './ParentOptInManager';

const api = {
  classes: (school) => apiUrl(`/admin/classes/${encodeURIComponent(school)}`),
  parents: (school) => apiUrl(`/admin/parents/${encodeURIComponent(school)}`),
  parentsByClass: (school, classId) => apiUrl(`/admin/parents/${encodeURIComponent(school)}/by-class/${encodeURIComponent(classId)}`),
  parentsStudentsByClass: (school, classId) => apiUrl(`/admin/parents-students/${encodeURIComponent(school)}/by-class/${encodeURIComponent(classId)}`),
  parentsStudentsCsvUpload: (school) => apiUrl(`/admin/parents-students/${encodeURIComponent(school)}/upload-csv`),
  parent: (school, parentId) => apiUrl(`/admin/parents/${encodeURIComponent(school)}/${encodeURIComponent(parentId)}`),
  studentsByClass: (school, classId) => apiUrl(`/admin/students/${encodeURIComponent(school)}/by-class/${encodeURIComponent(classId)}`),
  studentsAll: (school) => apiUrl(`/admin/students/${encodeURIComponent(school)}/all`),
  studentBase: (school) => apiUrl(`/admin/students/${encodeURIComponent(school)}`),
  studentItem: (school, id) => apiUrl(`/admin/students/${encodeURIComponent(school)}/${encodeURIComponent(id)}`)
};

const PARENT_STUDENT_TEMPLATE_CSV = [
  'Öğretmen Ad,Öğretmen Soyad,Öğretmen Telefon,Branş,,,',
  '',
  'Sınıf Ad,,,,,,',
  '',
  'Veli Ad,Veli Soyad,Veli Telefon,Öğrenci Ad,Öğrenci Soyad,Öğrenci No,Öğrenci Telefon'
].join('\r\n');

const classNameOf = (value) => (typeof value === 'object' ? (value?.name || '') : String(value || ''));

function FormRow({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{title}</Typography>
      {children}
    </Box>
  );
}

export default function ParentStudentManager({ schoolId, onError, onSuccess, user }) {
  const isTeacher = String(user?.role || '').toLowerCase() === 'teacher';
  const [activeTab, setActiveTab] = useState(0);
  const [parents, setParents] = useState([]);
  const [allParents, setAllParents] = useState([]);
  const [classParents, setClassParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [parentDialogOpen, setParentDialogOpen] = useState(false);
  const [studentDialogOpen, setStudentDialogOpen] = useState(false);

  // Combined creation dialog state (Parent + Student)
  const [comboOpen, setComboOpen] = useState(false);
  const [cpPhone, setCpPhone] = useState('');
  const [cpName, setCpName] = useState('');
  const [cpSurname, setCpSurname] = useState('');
  const [csName, setCsName] = useState('');
  const [csSurname, setCsSurname] = useState('');
  const [csSurnameTouched, setCsSurnameTouched] = useState(false);
  const [csClass, setCsClass] = useState('');
  const [csPhone, setCsPhone] = useState('');
  const [csStudentNo, setCsStudentNo] = useState('');
  const [comboBusy, setComboBusy] = useState(false);
  const [comboErrors, setComboErrors] = useState({});

  // Parent form state
  const [pPhone, setPPhone] = useState('');
  const [pName, setPName] = useState('');
  const [pSurname, setPSurname] = useState('');
  const [pStudentIds, setPStudentIds] = useState('');

  // Student form state
  const [sId, setSId] = useState('');
  const [sName, setSName] = useState('');
  const [sSurname, setSSurname] = useState('');
  const [sClass, setSClass] = useState('');
  const [sPhone, setSPhone] = useState('');
  const [sStudentNo, setSStudentNo] = useState('');
  const [sParentIds, setSParentIds] = useState([]);
  const [pPhoneError, setPPhoneError] = useState(false);
  const [sClassError, setSClassError] = useState(false);
  const [sStudentNoError, setSStudentNoError] = useState(false);

  // Quick-add dialogs
  const [quickParent, setQuickParent] = useState(null); // parent object to add student for
  const [quickStudentName, setQuickStudentName] = useState('');
  const [quickStudentSurname, setQuickStudentSurname] = useState('');
  const [quickStudentClass, setQuickStudentClass] = useState('');
  const [quickStudentPhone, setQuickStudentPhone] = useState('');
  const [quickStudentBusy, setQuickStudentBusy] = useState(false);

  const [quickStudent, setQuickStudent] = useState(null); // student object to add parent for
  const [quickParentPhone, setQuickParentPhone] = useState('');
  const [quickParentName, setQuickParentName] = useState('');
  const [quickParentSurname, setQuickParentSurname] = useState('');
  const [quickParentBusy, setQuickParentBusy] = useState(false);
  const [pendingStudentDelete, setPendingStudentDelete] = useState(null);
  const [deletingStudentId, setDeletingStudentId] = useState(null);

  // Loading states
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);
  const [loadingClassParents, setLoadingClassParents] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const csvInputRef = useRef(null);
  const classParentsRequestRef = useRef(0);

  const school = schoolId || '';

  const digitsOnly = (value) => String(value ?? '').replace(/[^0-9]/g, '');
  const findClassOption = useCallback((name) => classes.find((c) => classNameOf(c) === String(name || '')) || null, [classes]);
  const parseStudentNoInt = (value) => {
    const s = digitsOnly(value).trim();
    if (!s) return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  };

  const resetParentForm = () => {
    setPPhone(''); setPName(''); setPSurname(''); setPStudentIds(''); setPPhoneError(false);
  };
  const resetStudentForm = () => {
    setSId(''); setSName(''); setSSurname(''); setSClass(''); setSPhone(''); setSStudentNo(''); setSParentIds([]); setSClassError(false); setSStudentNoError(false);
  };

  // Resets for dialogs to avoid stale state on reopen
  const resetCombo = (prefillClass = '') => {
    setCpPhone('');
    setCpName('');
    setCpSurname('');
    setCsName('');
    setCsSurname('');
    setCsSurnameTouched(false);
    setCsClass(prefillClass || '');
    setCsPhone('');
    setCsStudentNo('');
    setComboErrors({});
  };
  const resetQuickStudent = (prefillClass = '') => {
    setQuickStudentName('');
    setQuickStudentSurname('');
    setQuickStudentClass(prefillClass || '');
    setQuickStudentPhone('');
  };
  const resetQuickParent = () => {
    setQuickParentPhone('');
    setQuickParentName('');
    setQuickParentSurname('');
  };
  const loadAllParents = useCallback(async () => {
    if (isTeacher) {
      setAllParents(parents);
      return;
    }
    try {
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const allRes = await fetchJson(api.parents(school), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (allRes.ok) {
        const all = await allRes.json();
        setAllParents(Array.isArray(all) ? all : []);
      } else {
        setAllParents([]);
      }
    } catch {
      setAllParents([]);
    }
  }, [school, isTeacher, parents]);

  const loadParentsForClass = useCallback(async (className) => {
    const requestId = ++classParentsRequestRef.current;
    const classOption = findClassOption(className);
    if (!classOption?.id) {
      setClassParents([]);
      setLoadingClassParents(false);
      return;
    }

    try {
      setLoadingClassParents(true);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const response = await fetchJson(api.parentsByClass(school, classOption.id), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) throw new Error('Veliler yüklenemedi');
      const data = await response.json();
      if (requestId === classParentsRequestRef.current) {
        setClassParents(Array.isArray(data) ? data : []);
      }
    } catch {
      if (requestId === classParentsRequestRef.current) {
        setClassParents([]);
        onError && onError('Veliler yüklenemedi');
      }
    } finally {
      if (requestId === classParentsRequestRef.current) setLoadingClassParents(false);
    }
  }, [school, onError, findClassOption]);

  const loadAllStudents = useCallback(async () => {
    try {
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const response = await fetchJson(api.studentsAll(school), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!response.ok) throw new Error('Öğrenciler yüklenemedi');
      const data = await response.json();
      setAllStudents(Array.isArray(data) ? data : []);
    } catch {
      setAllStudents([]);
      onError && onError('Öğrenciler yüklenemedi');
    }
  }, [school, onError]);

  const refresh = useCallback(async (clsOverride) => {
    try {
      setLoadingClasses(true);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const cls = await fetchJson(api.classes(school), { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => r.json()).catch(() => ({ classes: [] }));
      const classList = Array.isArray(cls) ? cls : (cls.classes || []);
      // Store full class objects with IDs
      setClasses(classList);
      setLoadingClasses(false);
      // effectiveClass can be class name or class ID now
      const effectiveClass = typeof clsOverride === 'string' || typeof clsOverride === 'number' ? clsOverride : selectedClass;
      setLoadingLists(true);
      if (effectiveClass) {
        // Find class object to get ID
        const classObj = typeof effectiveClass === 'number' 
          ? classList.find(c => c.id === effectiveClass)
          : classList.find(c => c.name === effectiveClass);
        const classId = classObj?.id;
        if (!classId) {
          setLoadingLists(false);
          return;
        }
        const ps = await fetchJson(api.parentsStudentsByClass(school, classId), { headers: token ? { Authorization: `Bearer ${token}` } : {} }).then(r => r.json());
        let baseParents = (ps && ps.parents) || [];
        setParents(baseParents);
        setStudents((ps && ps.students) || []);
      } else {
        setParents([]); setStudents([]);
        setAllParents([]);
      }
      setLoadingLists(false);
    } catch (e) {
      onError && onError('Veriler yüklenemedi');
      setLoadingClasses(false);
      setLoadingLists(false);
    }
  }, [onError, school, selectedClass]);

  const openStudentDialog = async (student) => {
    const className = student?.class_name || selectedClass || '';
    setSelectedStudent(student || null);
    if (!student) resetStudentForm();
    setSClass(className);
    setClassParents([]);
    await Promise.all([loadAllParents(), loadParentsForClass(className)]);
    setStudentDialogOpen(true);
  };

  const openParentDialog = async (parent) => {
    if (!parent) resetParentForm();
    setSelectedParent(parent || null);
    await loadAllStudents();
    setParentDialogOpen(true);
  };

  const uploadParentStudentCsv = useCallback(async (file) => {
    if (!file || !school) return;
    try {
      setCsvUploading(true);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(api.parentsStudentsCsvUpload(school), {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'CSV yüklenemedi');
      const nextClass = data?.class_info?.name || '';
      const processedRows = Number(data?.summary?.processed_rows || 0);
      const skippedRows = Number(data?.summary?.skipped_rows || 0);
      const rowErrors = Array.isArray(data?.row_errors) ? data.row_errors : [];
      const skippedDetails = rowErrors.slice(0, 3)
        .map((error) => `Satır ${error.row_number}: ${error.message}`)
        .join(' ');
      const message = `CSV içe aktarıldı. Yeni şube: ${nextClass}. ${processedRows} satır işlendi.`
        + (skippedRows > 0 ? ` ${skippedRows} satır atlandı. ${skippedDetails}` : '');
      if (nextClass) {
        setSelectedClass(nextClass);
      }
      setSelectedParent(null);
      setSelectedStudent(null);
      await refresh(nextClass || selectedClass);
      onSuccess && onSuccess(message);
    } catch (e) {
      onError && onError(e.message || 'CSV yüklenemedi');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  }, [onError, onSuccess, refresh, school, selectedClass]);

  const handleCsvButtonClick = () => {
    if (csvUploading) return;
    csvInputRef.current?.click();
  };

  const handleDownloadTemplate = () => {
    const blob = new Blob([`\uFEFF${PARENT_STUDENT_TEMPLATE_CSV}`], { type: 'text/csv;charset=utf-8;' });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = 'şablon.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  };

  const handleCsvFileChange = async (event) => {
    const nextFile = event.target.files?.[0];
    if (!nextFile) return;
    await uploadParentStudentCsv(nextFile);
  };

  useEffect(() => { if (school) refresh(); }, [school, selectedClass, refresh]);

  // Select helpers
  useEffect(() => {
    if (selectedParent) {
      setPPhone(selectedParent.phone || '');
      setPName(selectedParent.name || '');
      setPSurname(selectedParent.surname || '');
      setPStudentIds((selectedParent.student_ids || '').toString());
    } else {
      resetParentForm();
    }
  }, [selectedParent]);

  useEffect(() => {
    if (selectedStudent) {
      setSId(selectedStudent.id || '');
      setSName(selectedStudent.name || '');
      setSSurname(selectedStudent.surname || '');
      setSClass(selectedStudent.class_name || selectedClass || '');
      setSPhone(selectedStudent.phone || '');
      setSStudentNo(digitsOnly(selectedStudent.student_no ?? ''));
      setSStudentNoError(false);
      setSParentIds(Array.isArray(selectedStudent.parent_ids) ? selectedStudent.parent_ids : []);
    } else {
      resetStudentForm();
      setSClass(selectedClass || '');
    }
  }, [selectedStudent, selectedClass]);

  // CRUD ops
  const saveParent = async () => {
    try {
      if (!pPhone || !pPhone.trim()) {
        setPPhoneError(true);
        onError && onError('Telefon zorunludur');
        return;
      }
      const pPhoneNorm = normalizePhone(pPhone);
      const body = {
        phone: pPhoneNorm,
        name: pName,
        surname: pSurname,
        student_ids: (pStudentIds || '').split(',').map(x => x.trim()).filter(Boolean).map(x => parseInt(x, 10)).filter(n => Number.isFinite(n))
      };
      const method = selectedParent && selectedParent.id ? 'PUT' : 'POST';
      const url = method === 'POST' ? api.parents(school) : api.parent(school, selectedParent.id);
    const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
  const res = await fetchJson(url, { method, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Hata');
      onSuccess && onSuccess('Veli kaydı kaydedildi');
      setParentDialogOpen(false);
      setSelectedParent(null);
      refresh();
    } catch (e) { onError && onError('Veli kaydı kaydedilemedi'); }
  };

  const deleteParent = async (parentId) => {
    if (!window.confirm('Veli silinsin mi?')) return;
    try {
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const res = await fetchJson(
        api.parent(school, parentId),
        { method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) throw new Error('Hata');
      onSuccess && onSuccess('Veli silindi');
      if (selectedParent && String(selectedParent.id) === String(parentId)) setSelectedParent(null);
      refresh();
    } catch (e) { onError && onError('Veli silinemedi'); }
  };

  const saveStudent = async () => {
    try {
      if (!sClass || !sClass.trim()) {
        setSClassError(true);
        onError && onError('Şube seçilmelidir');
        return;
      }

      const rawNo = (sStudentNo || '').trim();
      const parsedNo = parseStudentNoInt(rawNo);
      if (rawNo && parsedNo === null) {
        setSStudentNoError(true);
        onError && onError('Öğrenci numarası sadece sayı olmalıdır');
        return;
      }

      const body = {
        id: sId || undefined,
        name: sName,
        surname: sSurname,
        class_name: sClass,
        phone: normalizePhone(sPhone),
        parent_ids: Array.isArray(sParentIds) ? sParentIds : []
      };
      if (parsedNo !== null) body.student_no = parsedNo;
      const exists = students.find(s => s.id === sId);
      const method = exists ? 'PUT' : 'POST';
      const url = exists ? api.studentItem(school, sId) : api.studentBase(school);
  const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
  const res = await fetchJson(url, { method, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Hata');
      onSuccess && onSuccess('Öğrenci kaydı kaydedildi');
      setStudentDialogOpen(false);
      setSelectedStudent(null);
      if (sClass) setSelectedClass(sClass);
      refresh(sClass || selectedClass);
    } catch (e) { onError && onError('Öğrenci kaydı kaydedilemedi'); }
  };

  const deleteStudent = async (id) => {
    try {
      setDeletingStudentId(id);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const res = await fetchJson(
        api.studentItem(school, id),
        { method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) throw new Error('Hata');
      onSuccess && onSuccess('Öğrenci silindi');
      if (selectedStudent && selectedStudent.id === id) setSelectedStudent(null);
      setPendingStudentDelete(null);
      refresh();
    } catch (e) { onError && onError('Öğrenci silinemedi'); }
    finally {
      setDeletingStudentId(null);
    }
  };

  // Combined create: Parent + Student, link both ways
  const createParentAndStudent = async () => {
    const errs = {};
    if (!cpPhone.trim()) errs.cpPhone = 'Telefon zorunlu';
    if (!csClass.trim()) errs.csClass = 'Şube zorunlu';
    if (!csName.trim() || !csSurname.trim()) errs.csName = 'Öğrenci adı/soyadı zorunlu';
    const rawNo = (csStudentNo || '').trim();
    const parsedNo = parseStudentNoInt(rawNo);
    if (rawNo && parsedNo === null) errs.csStudentNo = 'Öğrenci numarası sadece sayı olmalıdır';
    setComboErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      setComboBusy(true);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const pp = normalizePhone(cpPhone);
      // 1) Create parent
      const parentBody = { phone: pp, name: cpName, surname: cpSurname, student_ids: [] };
      const res = await fetchJson(api.parents(school), { method: 'POST', headers, body: JSON.stringify(parentBody) });
      if (!res.ok) throw new Error('Veli oluşturma başarısız');
      const createdParent = await res.json();
      const parentId = createdParent?.id;
      if (!parentId) throw new Error('Veli id alınamadı');

      // 2) Create student linked to this parent_id
      const studentBody = {
        name: csName,
        surname: csSurname,
        class_name: csClass,
        phone: normalizePhone(csPhone),
        parent_ids: [Number(parentId)]
      };
      if (parsedNo !== null) studentBody.student_no = parsedNo;
  let resStu = await fetchJson(api.studentBase(school), { method: 'POST', headers, body: JSON.stringify(studentBody) });
      if (!resStu.ok) throw new Error('Öğrenci oluşturma başarısız');
  onSuccess && onSuccess('Veli ve öğrenci oluşturuldu');
  const usedClass = csClass;
  setComboOpen(false);
  resetCombo('');
  refresh(usedClass || selectedClass);
    } catch (e) {
      onError && onError(e.message || 'Kayıt oluşturulamadı');
    } finally {
      setComboBusy(false);
    }
  };

  // Quick add student for a parent
  const quickAddStudentForParent = async () => {
    if (!quickParent) return;
    try {
      setQuickStudentBusy(true);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const studentBody = {
        name: quickStudentName,
        surname: quickStudentSurname,
        class_name: quickStudentClass,
        phone: normalizePhone(quickStudentPhone),
        parent_ids: [Number(quickParent.id)]
      };
  const res = await fetchJson(api.studentBase(school), { method: 'POST', headers, body: JSON.stringify(studentBody) });
      if (!res.ok) throw new Error('Öğrenci eklenemedi');
  onSuccess && onSuccess('Öğrenci eklendi');
  const usedClass = quickStudentClass;
  setQuickParent(null);
  resetQuickStudent('');
  refresh(usedClass || selectedClass);
    } catch (e) {
      onError && onError(e.message || 'Öğrenci eklenemedi');
    } finally {
      setQuickStudentBusy(false);
    }
  };

  // Quick add parent for a student
  const quickAddParentForStudent = async () => {
    if (!quickStudent) return;
    try {
      setQuickParentBusy(true);
      const token = JSON.parse(localStorage.getItem('adminUser') || '{}')?.token;
      const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
      const pp = normalizePhone(quickParentPhone);
      // create parent, then link by id
      const parentBody = { phone: pp, name: quickParentName, surname: quickParentSurname, student_ids: [] };
      const res = await fetchJson(api.parents(school), { method: 'POST', headers, body: JSON.stringify(parentBody) });
      if (!res.ok) throw new Error('Veli kaydedilemedi');
      const createdParent = await res.json();
      const newParentId = createdParent?.id;
      if (!newParentId) throw new Error('Veli id alınamadı');

      const existingIds = Array.isArray(quickStudent.parent_ids) ? quickStudent.parent_ids : [];
      const mergedIds = Array.from(new Set([...(existingIds || []), Number(newParentId)])).filter((x) => Number.isFinite(x));
      const sUpdateBody = { ...quickStudent, parent_ids: mergedIds };
      delete sUpdateBody.parents; // safety
  await fetchJson(api.studentItem(school, quickStudent.id), { method: 'PUT', headers, body: JSON.stringify(sUpdateBody) });
  onSuccess && onSuccess('Veli eklendi');
  const usedClass = quickStudent.class_name;
  setQuickStudent(null);
  resetQuickParent();
  refresh(usedClass || selectedClass);
    } catch (e) {
      onError && onError(e.message || 'Veli eklenemedi');
    } finally {
      setQuickParentBusy(false);
    }
  };

  const ParentChips2 = ({ studentIds, phone }) => {
    const entries = Array.isArray(studentIds) ? studentIds : [];
    return (
      <Box sx={{ mt: 0.5 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {phone ? <Chip size="small" label={`Veli ${phone}`} sx={{ mr: 0.5, mb: 0.5 }} /> : null}
          {entries.map((sid) => (
            <Chip key={String(sid)} size="small" label={`Öğrenci #${sid}`} sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
          {entries.length === 0 && !phone ? (
            <Typography variant="body2" color="text.secondary">Veli ilişkilendirilmemiş</Typography>
          ) : null}
        </Box>
      </Box>
    );
  };

  const StudentChips2 = ({ parentIds, studentId, studentNo }) => {
    const byId = new Map();
    (parents || []).forEach((p) => {
      if (p && p.id != null) byId.set(String(p.id), p);
    });
    (allParents || []).forEach((p) => {
      if (p && p.id != null && !byId.has(String(p.id))) byId.set(String(p.id), p);
    });
    const entries = Array.isArray(parentIds) ? parentIds : [];
    const hasStudentNo = studentNo !== null && studentNo !== undefined && String(studentNo) !== '';
    return (
      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap' }}>
        {studentId ? <Chip size="small" label={`Öğrenci #${studentId}`} sx={{ mr: 0.5, mb: 0.5 }} /> : null}
        {hasStudentNo ? <Chip size="small" color="primary" label={`No ${studentNo}`} sx={{ mr: 0.5, mb: 0.5 }} /> : null}
        {entries.map((pid) => {
          const p = byId.get(String(pid));
          const label = p ? `Veli ${p.phone || pid}` : `Veli #${pid}`;
          return <Chip key={String(pid)} size="small" label={label} sx={{ mr: 0.5, mb: 0.5 }} />;
        })}
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Veli ilişkilendirilmemiş</Typography>
        ) : null}
      </Box>
    );
  };

  const parentStudentOptions = selectedParent
    ? (allStudents || [])
    : (allStudents || []).filter((student) => String(student.class_name || '') === String(selectedClass || ''));
  const selectedParentIds = Array.isArray(sParentIds) ? sParentIds : [];
  const studentParentOptions = [...(classParents || [])];
  selectedParentIds.forEach((parentId) => {
    if (studentParentOptions.some((parent) => String(parent.id) === String(parentId))) return;
    studentParentOptions.push(
      [...(allParents || []), ...(parents || [])].find((parent) => String(parent.id) === String(parentId))
      || { id: parentId, phone: `Veli #${parentId}` }
    );
  });

  return (
    <>
      <Tabs
        value={activeTab}
        onChange={(_, nextTab) => setActiveTab(nextTab)}
        aria-label="Veli ve öğrenci yönetimi sekmeleri"
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Veli-Öğrenci Yönetimi" id="parent-student-tab-0" aria-controls="parent-student-tabpanel-0" />
        <Tab label="Veli kayıt durumu" id="parent-student-tab-1" aria-controls="parent-student-tabpanel-1" />
      </Tabs>

      <Box
        role="tabpanel"
        hidden={activeTab !== 0}
        id="parent-student-tabpanel-0"
        aria-labelledby="parent-student-tab-0"
      >
      {!isTeacher && (
        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleCsvFileChange}
        />
      )}
      <Grid container spacing={3}>
        {/* Class selector */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Autocomplete
                size="small"
                sx={{ minWidth: 220 }}
                options={classes}
                getOptionLabel={(option) => typeof option === 'string' ? option : (option?.name || '')}
                value={classes.find(c => (typeof c === 'object' ? c.name : c) === selectedClass) || null}
                onChange={(_, v) => { 
                  const val = v ? (typeof v === 'object' ? v.name : v) : ''; 
                  setSelectedClass(val); 
                  setSelectedParent(null); 
                  setSelectedStudent(null); 
                  refresh(val); 
                }}
                loading={loadingClasses}
                renderInput={(params) => (
                  <TextField {...params} label="Şube Seçin" placeholder="Yazdıkça filtrelenir" InputProps={{ ...params.InputProps, endAdornment: (
                    <>
                      {loadingClasses ? <Skeleton variant="circular" width={16} height={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ) }} />
                )}
                clearOnEscape
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={refresh}>Yenile</Button>
                {!isTeacher && <Button variant="outlined" size="small" onClick={handleDownloadTemplate}>Şablonu İndir</Button>}
                {!isTeacher && <Button variant="outlined" size="small" onClick={handleCsvButtonClick} disabled={csvUploading || !school}>{csvUploading ? 'CSV Yükleniyor...' : 'CSV Yükle'}</Button>}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderLeft: { xs: 0, sm: 1 }, borderColor: 'divider', pl: { xs: 0, sm: 1.5 }, ml: { xs: 0, sm: 0.5 } }}>
                  <Button variant="outlined" size="small" startIcon={<Add />} disabled={!selectedClass} onClick={() => openParentDialog(null)}>Veli Ekle</Button>
                  <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => openStudentDialog(null)}>Öğrenci Ekle</Button>
                  <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => { resetCombo(selectedClass || ''); setComboOpen(true); }}>Veli + Öğrenci Ekle</Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Parents */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Veliler</Typography>
              <Box />
            </Box>
            {loadingLists ? (
              <List dense>
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListItem key={i} disableGutters sx={{ px: 1, py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Skeleton variant="circular" width={36} height={36} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="40%" height={18} />
                        <Skeleton variant="text" width="70%" height={14} />
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <List dense>
              {parents.map((p) => (
                <ListItem
                  key={p.id}
                  button
                  selected={selectedParent?.id === p.id}
                  onClick={() => setSelectedParent(p)}
                  disableGutters
                  sx={{
                    px: 1,
                    py: 1.25,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{`${p.name} ${p.surname}`}</Typography>
                      <ParentChips2 studentIds={p.student_ids} phone={p.phone} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0, whiteSpace: 'nowrap' }}>
                      <IconButton edge="end" onClick={(e) => { e.stopPropagation(); openParentDialog(p); }}><Edit /></IconButton>
                      <IconButton edge="end" onClick={(e) => { e.stopPropagation(); deleteParent(p.id); }}><Delete /></IconButton>
                    </Box>
                  </Box>
                </ListItem>
              ))}
              {(!parents.length && selectedClass) ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Bu sınıf için veli bulunmuyor.</Typography>
                  <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openParentDialog(null)}>Veli Ekle</Button>
                </Box>
              ) : null}
              {!selectedClass ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">Önce bir sınıf seçin</Typography>
                </Box>
              ) : null}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Students */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Öğrenciler</Typography>
              <Box />
            </Box>
            {loadingLists ? (
              <List dense>
                {Array.from({ length: 6 }).map((_, i) => (
                  <ListItem key={i} disableGutters sx={{ px: 1, py: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Skeleton variant="circular" width={36} height={36} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="40%" height={18} />
                        <Skeleton variant="text" width="70%" height={14} />
                      </Box>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <List dense>
              {students.map((s) => (
                <ListItem key={s.id} button selected={selectedStudent?.id === s.id} onClick={() => setSelectedStudent(s)}>
                  <ListItemText
                    primary={<Typography variant="body1" sx={{ fontWeight: 500 }}>{`${s.name} ${s.surname}`}</Typography>}
                    secondary={<StudentChips2 parentIds={s.parent_ids} studentId={s.id} studentNo={s.student_no} />}
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => { openStudentDialog(s); }}><Edit /></IconButton>
                    <IconButton edge="end" onClick={() => setPendingStudentDelete(s)}><Delete /></IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {(!students.length && selectedClass) ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Bu sınıf için öğrenci bulunmuyor.</Typography>
                  <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openStudentDialog(null)}>Öğrenci Ekle</Button>
                </Box>
              ) : null}
              {!selectedClass ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">Önce bir sınıf seçin</Typography>
                </Box>
              ) : null}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Parent Dialog */}
      <Dialog open={parentDialogOpen} onClose={() => { setParentDialogOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Veli Formu</DialogTitle>
        <DialogContent dividers>
          <FormRow title="Telefon (benzersiz)"><TextField required error={pPhoneError} helperText={pPhoneError ? 'Telefon zorunludur' : ''} size="small" value={pPhone} onChange={(e) => { setPPhone(e.target.value); if (pPhoneError && e.target.value) setPPhoneError(false); }} fullWidth /></FormRow>
          <FormRow title="Ad"><TextField size="small" value={pName} onChange={(e) => setPName(e.target.value)} fullWidth /></FormRow>
          <FormRow title="Soyad"><TextField size="small" value={pSurname} onChange={(e) => setPSurname(e.target.value)} fullWidth /></FormRow>
          <FormRow title="Öğrenciler">
            <Autocomplete
              multiple
              fullWidth
              size="small"
              options={parentStudentOptions}
              value={parentStudentOptions.filter((student) => (pStudentIds || '').split(',').map((id) => id.trim()).includes(String(student.id)))}
              onChange={(_, value) => setPStudentIds((value || []).map((student) => student.id).join(','))}
              getOptionLabel={(student) => `${student.name || ''} ${student.surname || ''} · #${student.id}${student.class_name ? ` · ${student.class_name}` : ''}`.trim()}
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Öğrenci seçin"
                  placeholder="Ad veya numara ile arayın"
                  helperText={!selectedParent && !selectedClass ? 'Önce şube seçin' : !parentStudentOptions.length ? 'Bu şubede kayıtlı öğrenci bulunmuyor.' : ''}
                />
              )}
              clearOnEscape
            />
          </FormRow>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParentDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={saveParent}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* Student Dialog */}
      <Dialog open={studentDialogOpen} onClose={() => { setStudentDialogOpen(false); }} fullWidth maxWidth="sm">
        <DialogTitle>Öğrenci Formu</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
            <FormRow title="Şube">
                <Autocomplete
                  fullWidth
                  size="small"
                  options={classes}
                  getOptionLabel={(option) => classNameOf(option)}
                  isOptionEqualToValue={(a, b) => classNameOf(a) === classNameOf(b)}
                  value={findClassOption(sClass)}
                  onChange={(_, v) => {
                    const next = classNameOf(v);
                    setSClass(next);
                    if (sClassError && next) setSClassError(false);
                    loadParentsForClass(next);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Şube" error={sClassError} placeholder="Yazdıkça filtrelenir" />
                  )}
                  clearOnEscape
                />
              </FormRow>
            </Grid>
            <Grid item xs={12} sm={6}><FormRow title="Öğrenci Numarası (opsiyonel)"><TextField size="small" type="text" inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} error={sStudentNoError} helperText={sStudentNoError ? 'Sadece sayı girin' : ''} value={sStudentNo} onChange={(e) => { setSStudentNo(digitsOnly(e.target.value)); if (sStudentNoError) setSStudentNoError(false); }} fullWidth /></FormRow></Grid>
            <Grid item xs={12} sm={6}><FormRow title="Ad"><TextField size="small" value={sName} onChange={(e) => setSName(e.target.value)} fullWidth /></FormRow></Grid>
            <Grid item xs={12} sm={6}><FormRow title="Soyad"><TextField size="small" value={sSurname} onChange={(e) => setSSurname(e.target.value)} fullWidth /></FormRow></Grid>
            <Grid item xs={12}><FormRow title="Telefon"><TextField size="small" value={sPhone} onChange={(e) => setSPhone(e.target.value)} fullWidth /></FormRow></Grid>
            <Grid item xs={12}>
              <FormRow title="Veliler">
                <Autocomplete
                  multiple
                  fullWidth
                  size="small"
                  options={studentParentOptions}
                  value={studentParentOptions.filter((p) => selectedParentIds.some((parentId) => String(parentId) === String(p.id)))}
                  onChange={(_, v) => setSParentIds((v || []).map((p) => p.id))}
                  getOptionLabel={(p) => `${p.phone || ''}${p.name || p.surname ? ` • ${p.name || ''} ${p.surname || ''}` : ''}`.trim()}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Veli seçin"
                      placeholder={sClass ? 'Yazdıkça filtrelenir' : 'Önce şube seçin'}
                      helperText={!sClass ? 'Önce şube seçin' : loadingClassParents ? 'Veliler yükleniyor…' : !classParents.length ? 'Bu şubede kayıtlı veli bulunmuyor.' : ''}
                    />
                  )}
                  clearOnEscape
                />
              </FormRow>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStudentDialogOpen(false)}>İptal</Button>
          <Button variant="contained" onClick={saveStudent}>Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* Combined Parent + Student Dialog */}
  <Dialog open={comboOpen} onClose={() => { if (!comboBusy) { setComboOpen(false); resetCombo(''); } }} fullWidth maxWidth="sm">
        <DialogTitle>Veli + Öğrenci Ekle</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">Veli Bilgileri</Typography>
            </Grid>
            <Grid item xs={12} sm={6}><TextField label="Veli Telefon" required error={!!comboErrors.cpPhone} helperText={comboErrors.cpPhone || ''} size="small" fullWidth value={cpPhone} onChange={(e)=> setCpPhone(e.target.value)} /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Ad" size="small" fullWidth value={cpName} onChange={(e)=> setCpName(e.target.value)} /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Soyad" size="small" fullWidth value={cpSurname} onChange={(e)=> { setCpSurname(e.target.value); }} onBlur={()=> { setCsSurname(prev => { return (!csSurnameTouched || !prev) ? cpSurname : prev; }); }} /></Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Öğrenci Bilgileri</Typography>
            </Grid>
            <Grid item xs={12} sm={6}><TextField label="Ad" required error={!!comboErrors.csName} size="small" fullWidth value={csName} onChange={(e)=> setCsName(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Soyad" required error={!!comboErrors.csName} size="small" fullWidth value={csSurname} onChange={(e)=> { setCsSurname(e.target.value); setCsSurnameTouched(true); }} /></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                size="small"
                options={classes}
                getOptionLabel={(option) => classNameOf(option)}
                isOptionEqualToValue={(a, b) => classNameOf(a) === classNameOf(b)}
                value={findClassOption(csClass)}
                onChange={(_, v) => setCsClass(classNameOf(v))}
                renderInput={(params) => (
                  <TextField {...params} label="Şube" required error={!!comboErrors.csClass} placeholder="Yazdıkça filtrelenir" />
                )}
                clearOnEscape
              />
            </Grid>
            <Grid item xs={12} sm={6}><TextField label="Öğrenci Telefon (opsiyonel)" size="small" fullWidth value={csPhone} onChange={(e)=> setCsPhone(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Öğrenci Numarası (opsiyonel)" size="small" fullWidth type="text" inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} error={!!comboErrors.csStudentNo} helperText={comboErrors.csStudentNo || ''} value={csStudentNo} onChange={(e)=> setCsStudentNo(digitsOnly(e.target.value))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setComboOpen(false); resetCombo(''); }} disabled={comboBusy}>Vazgeç</Button>
          <Button variant="contained" onClick={createParentAndStudent} disabled={comboBusy}>{comboBusy ? 'Kaydediliyor...' : 'Kaydet'}</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Student for Parent Dialog */}
  <Dialog open={!!quickParent} onClose={() => { if (!quickStudentBusy) { setQuickParent(null); resetQuickStudent(''); } }} fullWidth maxWidth="sm">
        <DialogTitle>Öğrenci Ekle (Veli: {quickParent?.phone})</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField label="Ad" size="small" fullWidth value={quickStudentName} onChange={(e)=> setQuickStudentName(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}><TextField label="Soyad" size="small" fullWidth value={quickStudentSurname} onChange={(e)=> setQuickStudentSurname(e.target.value)} /></Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth size="small" options={classes}
                getOptionLabel={(option) => classNameOf(option)}
                isOptionEqualToValue={(a, b) => classNameOf(a) === classNameOf(b)}
                value={findClassOption(quickStudentClass)}
                onChange={(_, v)=> setQuickStudentClass(classNameOf(v))}
                renderInput={(params)=> (<TextField {...params} label="Şube" placeholder="Yazdıkça filtrelenir" />)}
              />
            </Grid>
            <Grid item xs={12} sm={6}><TextField label="Öğrenci Telefon (opsiyonel)" size="small" fullWidth value={quickStudentPhone} onChange={(e)=> setQuickStudentPhone(e.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQuickParent(null); resetQuickStudent(''); }} disabled={quickStudentBusy}>Vazgeç</Button>
          <Button variant="contained" onClick={quickAddStudentForParent} disabled={quickStudentBusy}>{quickStudentBusy ? 'Ekleniyor...' : 'Ekle'}</Button>
        </DialogActions>
      </Dialog>

      {/* Quick Add Parent for Student Dialog */}
  <Dialog open={!!quickStudent} onClose={() => { if (!quickParentBusy) { setQuickStudent(null); resetQuickParent(); } }} fullWidth maxWidth="sm">
        <DialogTitle>Veli Ekle (Öğrenci: #{quickStudent?.id} {quickStudent?.name} {quickStudent?.surname})</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><TextField label="Veli Telefon" size="small" fullWidth value={quickParentPhone} onChange={(e)=> setQuickParentPhone(e.target.value)} /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Ad" size="small" fullWidth value={quickParentName} onChange={(e)=> setQuickParentName(e.target.value)} /></Grid>
            <Grid item xs={12} sm={3}><TextField label="Soyad" size="small" fullWidth value={quickParentSurname} onChange={(e)=> setQuickParentSurname(e.target.value)} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQuickStudent(null); resetQuickParent(); }} disabled={quickParentBusy}>Vazgeç</Button>
          <Button variant="contained" onClick={quickAddParentForStudent} disabled={quickParentBusy}>{quickParentBusy ? 'Ekleniyor...' : 'Ekle'}</Button>
        </DialogActions>
      </Dialog>

      {/* Student Delete Confirm Dialog */}
      <Dialog open={Boolean(pendingStudentDelete)} onClose={() => { if (!deletingStudentId) setPendingStudentDelete(null); }}>
        <DialogTitle>Öğrenci Silinsin mi?</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {pendingStudentDelete
              ? `${pendingStudentDelete.name || ''} ${pendingStudentDelete.surname || ''} öğrencisi silinecek.`
              : 'Seçili öğrenci silinecek.'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingStudentDelete(null)} disabled={Boolean(deletingStudentId)}>Vazgeç</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!pendingStudentDelete || Boolean(deletingStudentId)}
            onClick={() => pendingStudentDelete && deleteStudent(pendingStudentDelete.id)}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      {activeTab === 1 ? (
        <Box role="tabpanel" id="parent-student-tabpanel-1" aria-labelledby="parent-student-tab-1">
          <ParentOptInManager
            schoolId={schoolId}
            onError={onError}
            onSuccess={onSuccess}
            readOnly={isTeacher}
          />
        </Box>
      ) : null}

    </>
  );
}
